import { useState } from "react"
import { Container, Text, Title } from "components/Form"
import { ConfigContainer } from "components"
import { isPasswordValid } from "utils/scripts/security"
import { useDispatch } from "react-redux"
import { customerAdministration, message } from "state_management"
import useT from "utils/hooks/useT"

export default function AdminInputConfig({ admin, customerId, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  // A Member has no `name` field — this used to read admin.name, so the form always opened empty
  // and sent a `name` the API silently discarded. `initials` is the Member's actual identifier.
  const [activeAdminInitials, setActiveAdminInitials] = useState(admin ? admin.initials : "")
  const [activeAdminEmail, setActiveAdminEmail] = useState(admin ? admin.email : "")
  const [activeAdminPassword, setActiveAdminPassword] = useState("")
  const isEdit = Boolean(admin)

  return (
    <ConfigContainer
      title={admin ? t("editAdmin") : t("addAdminTitle")}
      cancelHandler={onClose}
      submitHandler={() => {
        if (isEdit) {
          // Only `initials` is patchable. The endpoint excludes email and password from its schema,
          // and Member.email is a read-only property over the underlying user — sending either is
          // why editing an administrator never worked. Changing an administrator's e-mail is
          // deliberately NOT offered here: that is the identity-takeover path SPEC-051 restricts,
          // and it belongs behind the credentials endpoint's own rules.
          return dispatch(
            customerAdministration.editAdmin(admin.id, { initials: activeAdminInitials })
          ).then(onClose)
        }

        const passwordResult = isPasswordValid(activeAdminPassword)
        if (!passwordResult.valid) {
          const passwordMessage = "Missing password requirements: " + passwordResult.missingRequirements.join(", ")
          return dispatch(message.warning(passwordMessage))
        }
        return dispatch(
          customerAdministration.addAdmin(customerId, {
            initials: activeAdminInitials,
            email: activeAdminEmail,
            password: activeAdminPassword,
          })
        ).then(onClose)
      }}
    >
      <Container>
        <Title>{t("initials")}</Title>
        <Text
          required
          textTransform="uppercase"
          value={activeAdminInitials}
          onChange={(e) => setActiveAdminInitials(e)}
        />
      </Container>
      <Container>
        <Title>{t("email")}</Title>
        {/* Read-only when editing: the API cannot change it here (see submitHandler). */}
        <Text
          required={!isEdit}
          disabled={isEdit}
          type="email"
          value={activeAdminEmail}
          onChange={isEdit ? undefined : (e) => setActiveAdminEmail(e)}
        />
      </Container>
      {!isEdit && (
        <Container>
          <Title>{t("password")}</Title>
          <Text required type="password" value={activeAdminPassword} onChange={(e) => setActiveAdminPassword(e)} />
        </Container>
      )}
    </ConfigContainer>
  )
}
